/**
 * Factory API Transformers
 * Converts between API DTOs and domain types.
 * Uses Strapi's native i18n with ?populate=localizations to merge translations.
 */

import { mergeLocalizations, getLocalizedValue } from '@ufc/core'
import type {
  Factory,
  FactoryListItem,
  FactorySummary,
  City,
  Country,
  Location,
  LocalizedText,
  CreateFactoryRequest,
  UpdateFactoryRequest,
} from '../domain/types'
import type {
  FactoryDto,
  CityDto,
  CountryDto,
  LocationDto,
  CreateFactoryRequestDto,
} from './api-types'

// ============================================================================
// City Transformers
// ============================================================================

export function transformCityDtoToCity(dto: CityDto): City {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name: dto.name,
    stateProvince: dto.stateProvince ?? undefined,
    countryId: dto.country ? String(dto.country.id) : undefined,
    isActive: dto.isActive,
    locale: dto.locale,
    localizations: dto.localizations,
  }
}

// ============================================================================
// Country Transformers
// ============================================================================

export function transformCountryDtoToCountry(dto: CountryDto): Country {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name: dto.name,
    isActive: dto.isActive,
  }
}

// ============================================================================
// Location Transformers
// ============================================================================

export function transformLocationDtoToLocation(dto: LocationDto): Location {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name: dto.name,
    addressLine1: dto.addressLine1 ?? undefined,
    addressLine2: dto.addressLine2 ?? undefined,
    postalCode: dto.postalCode ?? undefined,
    timezone: dto.timezone ?? undefined,
    latitude: dto.latitude ?? undefined,
    longitude: dto.longitude ?? undefined,
    status: dto.status,
    city: dto.city ? transformCityDtoToCity(dto.city) : undefined,
    country: dto.country ? transformCountryDtoToCountry(dto.country) : undefined,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  }
}

// ============================================================================
// Factory Transformers
// ============================================================================

/**
 * Transform FactoryDto to Factory domain entity.
 * Merges localizations from Strapi's i18n response.
 */
export function transformFactoryDtoToFactory(dto: FactoryDto): Factory {
  // Merge localizations using shared utility
  const name = mergeLocalizations(dto, 'name') as LocalizedText
  const description = dto.description
    ? mergeLocalizations(dto, 'description') as LocalizedText
    : undefined

  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    sapPlantCode: dto.sapPlantCode ?? undefined,
    name,
    description,
    status: dto.factoryStatus,
    metadata: dto.metadata ?? undefined,
    location: dto.location ? transformLocationDtoToLocation(dto.location) : undefined,
    cities: dto.cities ? dto.cities.map(transformCityDtoToCity) : undefined,
    deletedAt: dto.deletedAt ?? undefined,
    deletedBy: dto.deletedBy ?? undefined,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  }
}

/**
 * Transform Factory to FactoryListItem for UI lists.
 * Uses getLocalizedValue to resolve text based on current locale.
 */
export function transformFactoryToListItem(factory: Factory, locale: string = 'en'): FactoryListItem {
  const name = getLocalizedValue(factory.name, locale)
  const description = getLocalizedValue(factory.description, locale)

  // Get address from location
  const location = factory.location
  const addressParts = [location?.addressLine1, location?.addressLine2].filter(Boolean)
  const address = addressParts.join(', ')
  const cityNames = getFactoryCityNames(factory)

  return {
    id: factory.id,
    documentId: factory.documentId,
    code: factory.code,
    sapPlantCode: factory.sapPlantCode,
    name,
    description,
    locationName: location?.name,
    address,
    city: cityNames.join(', '),
    cities: cityNames,
    country: location?.country?.name ?? '',
    countryCode: location?.country?.code,
    coordinates:
      location?.latitude && location?.longitude
        ? { lat: location.latitude, lng: location.longitude }
        : undefined,
    locationDocumentId: location?.documentId,
    status: factory.status,
    isActive: factory.status === 'active',
    createdAt: factory.createdAt,
    updatedAt: factory.updatedAt,
  }
}

function getFactoryCityNames(factory: Factory): string[] {
  const locationCity = factory.location?.city?.name?.trim()
  const servedCities = (factory.cities ?? [])
    .map((city) => city.name?.trim())
    .filter((name): name is string => Boolean(name))

  const allCities = [locationCity, ...servedCities].filter(
    (name): name is string => Boolean(name)
  )

  const uniqueCities = new Map<string, string>()
  allCities.forEach((name) => {
    const normalized = name.toLowerCase()
    if (!uniqueCities.has(normalized)) {
      uniqueCities.set(normalized, name)
    }
  })

  return Array.from(uniqueCities.values())
}

/**
 * Transform FactoryDto directly to FactoryListItem
 * Shortcut for list operations
 */
export function transformFactoryDtoToListItem(dto: FactoryDto): FactoryListItem {
  const factory = transformFactoryDtoToFactory(dto)
  return transformFactoryToListItem(factory, dto.locale)
}

/**
 * Transform Factory to FactorySummary for header dropdown.
 * Used in Gate Portal factory selector.
 */
export function transformFactoryToSummary(factory: Factory, locale: string = 'en'): FactorySummary {
  return {
    id: factory.id,
    documentId: factory.documentId,
    code: factory.code,
    sapPlantCode: factory.sapPlantCode,
    name: getLocalizedValue(factory.name, locale),
    status: factory.status,
  }
}

/**
 * Transform FactoryDto directly to FactorySummary
 * Shortcut for header dropdown
 */
export function transformFactoryDtoToSummary(dto: FactoryDto): FactorySummary {
  const factory = transformFactoryDtoToFactory(dto)
  return transformFactoryToSummary(factory, dto.locale)
}

/**
 * Transform FactoryListItem to FactorySummary
 * For converting existing list items to summary format
 */
export function transformListItemToSummary(item: FactoryListItem): FactorySummary {
  return {
    id: item.id,
    documentId: item.documentId,
    code: item.code,
    name: item.name,
    status: item.status,
  }
}

// ============================================================================
// Request Transformers
// ============================================================================

/**
 * Transform CreateFactoryRequest to Strapi request format
 */
export function transformCreateRequestToDto(
  request: CreateFactoryRequest
): CreateFactoryRequestDto {
  return {
    code: request.code ?? generateFactoryCode(),
    sapPlantCode: request.sapPlantCode,
    name: request.name.en,
    description: request.description?.en,
    factoryStatus: 'active',
    metadata: request.metadata,
    // locationId is a documentId (string), use it directly for Strapi v5
    // Strapi will resolve documentId to the actual relation
    location: request.locationId ? request.locationId : undefined,
    // cityIds are documentIds for the many-to-many cities relation
    cities: request.cityIds,
  }
}

/**
 * Transform UpdateFactoryRequest to Strapi request format
 */
export function transformUpdateRequestToDto(
  request: UpdateFactoryRequest
): Partial<CreateFactoryRequestDto> {
  const dto: Partial<CreateFactoryRequestDto> = {}

  if (request.code) dto.code = request.code.trim().toUpperCase()
  if (request.sapPlantCode !== undefined) dto.sapPlantCode = request.sapPlantCode
  if (request.name) dto.name = request.name.en
  if (request.description) dto.description = request.description.en
  if (request.locationId !== undefined) {
    // locationId is a documentId (string), use it directly for Strapi v5
    // Strapi will resolve documentId to the actual relation
    dto.location = request.locationId
  }
  if (request.cityIds !== undefined) {
    // cityIds are documentIds for the many-to-many cities relation
    dto.cities = request.cityIds
  }
  if (request.status) dto.factoryStatus = request.status
  if (request.metadata !== undefined) dto.metadata = request.metadata

  return dto
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Generate a unique factory code
 */
function generateFactoryCode(): string {
  const timestamp = Date.now().toString(36).toUpperCase()
  const random = Math.random().toString(36).substring(2, 6).toUpperCase()
  return `FAC-${timestamp}-${random}`
}
